"use client";

/**
 * Step 5: Success Confirmation
 * Show booking details and next steps
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { BookingResponse } from "@/types/booking-v2";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  Copy,
  DollarSign,
  ExternalLink,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface SuccessStepProps {
  booking: BookingResponse;
  tenantSlug?: string; // unused but kept for potential future use
  onClose: () => void;
}

export function SuccessStep({
  booking,
  tenantSlug: _tenantSlug,
  onClose,
}: SuccessStepProps) {
  const [pixKeyCopied, setPixKeyCopied] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const copyPixKey = async () => {
    if (!booking.bookingFee?.pixKey) return;

    try {
      await navigator.clipboard.writeText(booking.bookingFee.pixKey);
      setPixKeyCopied(true);
      toast.success("Chave PIX copiada!");
      setTimeout(() => setPixKeyCopied(false), 3000);
    } catch {
      toast.error("Não foi possível copiar a chave PIX");
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Olá! Acabei de fazer um agendamento (ID: ${booking.id}). Gostaria de confirmar os detalhes.`
    );
    const tenantPhoneUnformatted = booking.tenantPhone.replace(/\D/g, "");
    window.open(
      `https://wa.me/55${tenantPhoneUnformatted}?text=${message}`,
      "_blank"
    );
  };

  return (
    <div className="space-y-6">
      {/* Success icon */}
      <div className="flex flex-col items-center text-center space-y-4">
        <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-950 flex items-center justify-center">
          <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h3 className="text-2xl font-bold mb-2">Agendamento Confirmado!</h3>
          <p className="text-muted-foreground">
            ID do agendamento:{" "}
            <code className="font-mono text-xs">{booking.id}</code>
          </p>
        </div>
      </div>

      {/* Booking details */}
      <Card className="p-4 bg-muted/50">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Data</p>
              <p className="font-semibold">
                {format(new Date(booking.startAt), "dd/MM/yyyy", {
                  locale: ptBR,
                })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Horário</p>
              <p className="font-semibold">
                {format(new Date(booking.startAt), "HH:mm", { locale: ptBR })}
                {" - "}
                {format(new Date(booking.endAt), "HH:mm", { locale: ptBR })}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDuration(booking.totalDuration)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Valor Total</p>
              <p className="font-semibold">
                {formatCurrency(booking.totalPrice / 100)}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Status badge */}
      <div className="flex justify-center">
        <Badge
          variant={
            booking.status === "PENDING_PAYMENT_CONFIRMATION"
              ? "destructive"
              : "secondary"
          }
          className="text-sm px-4 py-2"
        >
          {booking.status === "PENDING_PAYMENT_CONFIRMATION"
            ? "⏳ Aguardando Pagamento"
            : "⏳ Aguardando Confirmação"}
        </Badge>
      </div>

      {/* Booking fee payment */}
      {booking.bookingFee && (
        <Card className="p-4 border-2 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900 dark:text-amber-100">
                  Pagamento da Taxa de Reserva
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  Para confirmar seu agendamento, realize o pagamento da taxa no
                  valor de{" "}
                  <strong>
                    {formatCurrency(booking.bookingFee.amount / 100)}
                  </strong>{" "}
                  via PIX.
                </p>
              </div>
            </div>

            <div className="bg-background rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium">Chave PIX:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-muted px-3 py-2 rounded text-sm break-all">
                  {booking.bookingFee.pixKey}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyPixKey}
                  className="flex-shrink-0"
                >
                  {pixKeyCopied ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <p className="text-xs text-amber-800 dark:text-amber-200">
              💡 Após realizar o pagamento, envie o comprovante pelo WhatsApp
              para confirmação.
            </p>
          </div>
        </Card>
      )}

      {/* Next steps */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <h4 className="font-semibold mb-3 text-blue-900 dark:text-blue-100">
          📋 Próximos Passos
        </h4>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
          <li className="flex items-start gap-2">
            <span>1️⃣</span>
            <span>
              {booking.bookingFee
                ? "Realize o pagamento da taxa de reserva via PIX"
                : "Aguarde a confirmação do estabelecimento"}
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span>2️⃣</span>
            <span>
              {booking.bookingFee
                ? "Envie o comprovante pelo WhatsApp"
                : "Você receberá uma mensagem de confirmação"}
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span>3️⃣</span>
            <span>Compareça no horário agendado</span>
          </li>
        </ul>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          onClick={openWhatsApp}
          className="flex-1 h-12"
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          Falar no WhatsApp
        </Button>
        <Button onClick={onClose} className="flex-1 h-12">
          Fechar
        </Button>
      </div>

      {/* Additional info */}
      <p className="text-xs text-center text-muted-foreground">
        Você também receberá os detalhes por mensagem
      </p>
    </div>
  );
}
